import { Typography } from "@povio/ui";
import { cx } from "class-variance-authority";
import clsx from "clsx";
import { useRouter } from "next/router";

import { LiveshowIcon } from "@/assets/icons/general/LiveshowIcon";
import { LogoSmartIcon } from "@/assets/icons/navbar/LogoSmart";
import Notifications from "@/components/notifications/Notifications";
import { UserAvatarNavSection } from "@/components/shared/layout/navbar/UserAvatarNavSection";
import { TreatsButton } from "@/components/shared/layout/sidebar/TreatsButton";
import { Link } from "@/components/ui/text/Link/Link";
import { RouteConfig } from "@/config/route.config";
import { TourSelectors } from "@/config/tour.config";
import { type NavLink, useNavLinks } from "@/hooks/useNavLinks";
import { useTour } from "@/hooks/useTour";
import { DemoContext } from "@/providers/demo/DemoContext";
import { useLiveshowsStore } from "@/providers/liveshows/liveshows.store";

interface IProps {
  className?: string;
}

export const isPathActive = (href: string, pathname: string) => {
  // Remove query parameters and hash
  const pathWithoutQuery = pathname.split("?")[0].split("#")[0];

  // Normalize both paths by removing trailing slashes for comparison
  const normalizeSlash = (path: string) => (path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path);

  const normalizedHref = normalizeSlash(href);
  const normalizedPathname = normalizeSlash(pathWithoutQuery);

  // Special case: when on home page ("/"), show profile as active
  if (normalizedPathname === "/" && (normalizedHref === "/profile" || normalizedHref === "/demo/profile")) {
    return true;
  }

  return normalizedPathname === normalizedHref;
};

export const Sidebar = ({ className = "" }: IProps) => {
  const router = useRouter();
  const links = useNavLinks();

  return (
    <aside
      className={cx(
        `no-print fixed top-0 left-0 flex h-screen w-[320px] min-w-[320px] flex-col items-center justify-start bg-elevation-fill-default-0`,
        className,
      )}
    >
      {/* Logo, dogcoin and notifications */}
      <div className="flex w-full flex-row items-center justify-between p-2 pl-5">
        <div
          onClick={() => router.push(RouteConfig.Home)}
          className="cursor-pointer"
        >
          <LogoSmartIcon className="h-auto w-[140px]" />
        </div>

        <div className="flex items-center justify-center gap-2 p-button-height-xs">
          <TreatsButton />
          <Notifications />
        </div>
      </div>

      <div className="flex w-full flex-col items-start justify-center">
        <div className="flex max-h-[calc(100vh-176px)] w-full flex-1 flex-col items-start justify-start overflow-y-auto">
          <NavigationLinks links={links} />
        </div>
        <div
          className="fixed bottom-0 left-0 w-[320px] p-6"
          onClick={() => router.push(RouteConfig.Settings)}
          id={TourSelectors.UserAvatarNavSection}
        >
          <UserAvatarNavSection />
        </div>
      </div>
    </aside>
  );
};

export const NavigationLinks = ({
  links,
  onLinkClick,
  isMobile,
}: {
  links: NavLink[];
  onLinkClick?: (href: string) => void;
  isMobile?: boolean;
}) => {
  const { hasActiveLiveshow } = useLiveshowsStore();
  const { isTourActive } = useTour();
  const router = useRouter();
  const { isDemoMode } = DemoContext.useDemo();

  const handleLinkClick = (e: React.MouseEvent, link: NavLink) => {
    onLinkClick?.(link.href);
    if (link.isExternal) {
      e.preventDefault();
      window.open(link.href, "_blank");
      return;
    }
    // All links work normally now - no subscription modal triggers
  };
  const getTourSelector = (href: string) => {
    if (href === RouteConfig.Demo.Watchlist) {
      return TourSelectors.WatchlistSection;
    }
    if (href === RouteConfig.Demo.Analytics) {
      return TourSelectors.AnalyticsSection;
    }
    if (href === RouteConfig.Demo.MyRewards) {
      return TourSelectors.RewardsSection;
    }
    if (href === RouteConfig.Demo.CeoVsEmployee) {
      return TourSelectors.CeoVsEmployeeSection;
    }
    if (href === RouteConfig.Demo.Portfolio) {
      return TourSelectors.PortfolioSection;
    }
    if (href === RouteConfig.Demo.Liveshow) {
      return TourSelectors.LiveshowSection;
    }
    return undefined;
  };
  return (
    <div className={clsx("flex w-full flex-col", { "my-auto!": isMobile }, { "px-0 t:px-1": isDemoMode })}>
      {links.map((link) => {
        return (
          <Link
            key={link.href}
            href={link.href}
            className={clsx("no-underline! px-6 py-4 outline-hidden!", {
              "pointer-events-none": isTourActive,
            })}
            onClick={(e) => handleLinkClick(e, link)}
            id={getTourSelector(link.href)}
          >
            <Typography
              size="label-1"
              className={cx(
                "flex flex-row items-center gap-2 font-medium hover:text-interactive-contained-primary-hover active:text-interactive-contained-primary-pressed",
                isPathActive(link.href, router.asPath)
                  ? "text-interactive-contained-primary-idle"
                  : "text-text-default-1",
              )}
            >
              {link.label}
              {link.href === RouteConfig.Liveshow && hasActiveLiveshow && (
                <LiveshowIcon className="h-4 w-4 text-danger-200" />
              )}
            </Typography>
          </Link>
        );
      })}
    </div>
  );
};
